/**
 * The single fetch wrapper every API call in this app goes through, and the
 * one place where "the server said no" is told apart from "the server was not
 * there".
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL
 * ─────────────────────────────────────────────────────────────────────────
 * Better Auth's session is database-backed: the cookie is an opaque token and
 * validating it is a network round trip. So the naive client treats *any*
 * failure to reach `/get-session` as "not authenticated" and bounces to a
 * login screen — which, in a hall with no signal, it also cannot service. That
 * is the app failing at precisely the moment it exists for.
 *
 * The inverse mistake is just as real: if a genuine 401 does not clear the
 * local session, a device revoked from the admin path keeps working.
 *
 * So every outcome is classified into exactly one of four kinds, and callers
 * branch on the kind rather than on a thrown error or on message text
 * (coding-standards.md, Errors). Nothing in this module throws for a non-2xx
 * response; a `try/catch` around it would collapse the distinction it exists
 * to draw.
 */
import { ErrorEnvelope, errorCodeForStatus, type ErrorCode } from '@paradise/shared';

/**
 * Why we believe we are offline. All three mean the same thing to the session
 * layer — keep the cached session — but they read differently in a log and in
 * the login screen's copy.
 */
export type OfflineReason =
  /** `fetch` rejected: DNS, TLS, refused connection, airplane mode. */
  | 'network'
  /** The request was aborted by our own deadline. */
  | 'timeout'
  /** We reached *something*, but it returned 5xx — the app is not usable. */
  | 'server-error';

export type ApiResult<T> =
  | { kind: 'ok'; data: T }
  /**
   * Authoritative, and the ONLY kind that may clear a cached session. Means
   * the server was reached, understood the request, and rejected the caller's
   * identity: 401, 403, or Better Auth's `null` session body (see
   * `unauthenticatedFromNullBody`).
   */
  | { kind: 'unauthenticated'; status: number }
  /**
   * The server was reached and refused the request for a reason unrelated to
   * identity — validation, conflict, rate limit.
   */
  | { kind: 'error'; code: ErrorCode; message: string; status: number }
  /** The server was not usefully reached. NEVER means logged out. */
  | { kind: 'offline'; reason: OfflineReason };

/**
 * Base URL for API calls.
 *
 * Empty string by default, which means every request is a same-origin relative
 * path. That is correct in production (Fastify serves the built SPA from the
 * same :3100 origin) AND in development, because vite.config.ts proxies
 * `/api` and `/media` to the API rather than pointing the browser at a second
 * origin. See the "Dev origins" comment in vite.config.ts for why the proxy
 * was chosen over a cross-origin base URL.
 *
 * `VITE_API_BASE_URL` remains an escape hatch for pointing a local SPA at a
 * deployed API; setting it re-introduces cross-origin, and therefore needs
 * CORS `credentials: true` plus `trustedOrigins` on the server.
 */
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

/** Default per-request deadline. Long enough for a slow hall wifi, short enough that boot is not held hostage. */
export const DEFAULT_TIMEOUT_MS = 10_000;

/** Structural, so this module needs no zod import; every `@paradise/shared` schema satisfies it. */
export interface ResponseSchema<T> {
  safeParse: (value: unknown) => { success: true; data: T } | { success: false };
}

export interface RequestOptions<T = unknown> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Serialised as JSON. Omit for GET. */
  body?: unknown;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Validates a 2xx body before it is returned as `ok` — see `classifyResponse`. */
  schema?: ResponseSchema<T>;
}

/**
 * Pulls `{ error: { code, message } }` out of a response body when the API
 * sent our canonical envelope.
 *
 * Better Auth's own routes do NOT use that envelope — they answer with their
 * own `{ code, message }` shape — so this returns undefined for them and the
 * caller falls back to the status-derived code. That fallback is why callers
 * may branch on `ErrorCode` uniformly regardless of which half of the server
 * answered.
 */
function parseErrorEnvelope(payload: unknown): { code: ErrorCode; message: string } | undefined {
  const parsed = ErrorEnvelope.safeParse(payload);
  if (!parsed.success) return undefined;
  return { code: parsed.data.error.code, message: parsed.data.error.message };
}

/** Best-effort message extraction from a non-envelope body (Better Auth's `{ code, message }`). */
function looseMessage(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const record: Record<string, unknown> = { ...payload };
  const message = record['message'];
  return typeof message === 'string' && message.length > 0 ? message : undefined;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A non-JSON body (an HTML error page from something in front of the API)
    // is not something a caller can branch on; treat it as no body.
    return undefined;
  }
}

/**
 * Turns a settled `Response` into an `ApiResult`. This is the ONE place the
 * 5xx / 401 / 403 / error-envelope / ok classification lives, so JSON and
 * multipart requests draw the offline-vs-logged-out line identically.
 *
 * Without `schema` the success branch is an unchecked `payload as T`. One 200
 * with an unparseable body was enough to throw inside the sync engine's Dexie
 * transaction and take down a whole drain pass — three queued leads, zero
 * synced. A 2xx that does not match its schema classifies as
 * `offline`/`server-error` rather than `error`, because the likeliest producer
 * in the field is a captive-portal splash page: we reached something, and it
 * was not our API. Transient, so it retries instead of poisoning the queue.
 */
async function classifyResponse<T>(response: Response, schema: ResponseSchema<T> | undefined): Promise<ApiResult<T>> {
  // 5xx: something answered, but the app cannot be used. Explicitly NOT a
  // logout — a crashed API must not sign out an entire tradeshow floor.
  if (response.status >= 500) {
    return { kind: 'offline', reason: 'server-error' };
  }

  // The authoritative logout signal, and the only one.
  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthenticated', status: response.status };
  }

  const payload = await readJson(response);

  if (!response.ok) {
    const envelope = parseErrorEnvelope(payload);
    if (envelope) return { kind: 'error', ...envelope, status: response.status };
    return {
      kind: 'error',
      code: errorCodeForStatus(response.status),
      message: looseMessage(payload) ?? `Request failed with status ${String(response.status)}`,
      status: response.status
    };
  }

  if (schema) {
    const parsed = schema.safeParse(payload);
    if (!parsed.success) return { kind: 'offline', reason: 'server-error' };
    return { kind: 'ok', data: parsed.data };
  }

  return { kind: 'ok', data: payload as T };
}

/** Wraps `fetch` with our timeout/abort handling and the offline classification of a rejection. */
async function fetchClassified<T>(
  path: string,
  init: RequestInit,
  timeoutMs: number,
  signal: AbortSignal | undefined,
  schema: ResponseSchema<T> | undefined
): Promise<ApiResult<T>> {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  // Combining lets a caller cancel (route change, unmount) while our own
  // deadline still applies. The two are distinguished after the fact by asking
  // the timeout signal whether it fired.
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, signal: combinedSignal });
  } catch (cause) {
    // fetch rejects for network-layer failures and for aborts. Neither says
    // anything at all about whether we are still authenticated.
    if (timeoutSignal.aborted) return { kind: 'offline', reason: 'timeout' };
    if (signal?.aborted) throw cause; // caller-initiated cancellation; not our concern
    return { kind: 'offline', reason: 'network' };
  }

  return classifyResponse<T>(response, schema);
}

/**
 * Performs a JSON request and classifies the outcome. Never throws for a server
 * response; only a programming error inside this function could escape.
 */
export async function apiRequest<T>(path: string, options: RequestOptions<T> = {}): Promise<ApiResult<T>> {
  const { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS, signal, schema } = options;

  return fetchClassified<T>(
    path,
    {
      method,
      // Load-bearing. Same-origin in production and behind the dev proxy, so
      // this is a no-op there — but it is what makes the session cookie ride
      // along if VITE_API_BASE_URL ever points at another origin, and removing
      // it would break that path silently.
      credentials: 'include',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body)
    },
    timeoutMs,
    signal,
    schema
  );
}

/**
 * Performs a `multipart/form-data` request and classifies the outcome exactly
 * as `apiRequest` does. This is the path a queued `lead.create` takes when the
 * sync engine drains it: the JSON `payload` field and the card-photo file ride
 * in ONE request under ONE idempotency key (docs/plan.md, "The card photo
 * travels inside the lead mutation").
 *
 * The `content-type` header is deliberately NOT set — the browser must set it
 * itself so it can append the multipart boundary. A hand-set header omits the
 * boundary and the server cannot parse the body.
 */
export async function apiRequestMultipart<T>(
  path: string,
  form: FormData,
  options: { method?: 'POST' | 'PATCH'; timeoutMs?: number; signal?: AbortSignal; schema?: ResponseSchema<T> } = {}
): Promise<ApiResult<T>> {
  // A card upload is ~200 KB; give it longer than the default JSON deadline so
  // a slow hall wifi does not turn a real upload into a spurious "timeout".
  const { method = 'POST', timeoutMs = 30_000, signal, schema } = options;

  return fetchClassified<T>(
    path,
    { method, credentials: 'include', headers: { accept: 'application/json' }, body: form },
    timeoutMs,
    signal,
    schema
  );
}

/**
 * Re-classifies a 200 response whose body is `null` as authoritatively
 * unauthenticated.
 *
 * This is NOT a general rule — it exists for exactly one endpoint. Better
 * Auth's `GET /api/auth/get-session` answers **200 with a JSON `null` body**
 * when there is no valid session; it does not 401. (Verified against the
 * installed better-auth 1.6.25 source, `dist/api/routes/session.mjs`: the
 * handler `return null`s on a missing or invalid session cookie.) A client
 * that only watches for 401 would therefore treat a signed-out device as
 * merely "no data yet" and never clear its cache — the revoked-device failure
 * mode, arrived at from the opposite direction.
 */
export function unauthenticatedFromNullBody<T>(result: ApiResult<T | null>): ApiResult<T> {
  if (result.kind === 'ok' && result.data === null) {
    return { kind: 'unauthenticated', status: 200 };
  }
  return result as ApiResult<T>;
}

/** Per-call copy overrides for {@link describeApiFailure}. `error` always uses the server's own message. */
export interface ApiFailureCopy {
  /** Shown when the server was not usefully reached. Default: generic "Lost connection…". */
  offline?: string;
  /** Shown when the session is expired/invalid. Default: generic "session has expired…". */
  unauthenticated?: string;
}

/**
 * One user-facing message for a non-`ok` `ApiResult`. `error` carries the
 * server's own message; `offline`/`unauthenticated` get sensible defaults each
 * caller may override with context-specific copy (e.g. "…while saving"). This
 * is the one place the offline/unauthenticated/error → message mapping lives —
 * callers still branch on `kind` for control flow, never on the returned text.
 */
export function describeApiFailure(
  result: Exclude<ApiResult<unknown>, { kind: 'ok' }>,
  copy: ApiFailureCopy = {}
): string {
  switch (result.kind) {
    case 'offline':
      return copy.offline ?? 'Lost connection. Try again once you are back online.';
    case 'unauthenticated':
      return copy.unauthenticated ?? 'Your session has expired. Sign in again to continue.';
    case 'error':
      return result.message;
  }
}
